import type { ClientToSWMessage, LoadStatus, SWToClientMessage } from '../worker/message-types.js';
import { STATE_CHANNEL_NAME } from '../worker/message-channel.js';

type MessageHandler = (message: SWToClientMessage) => void;
type OrgHandler = (message: Extract<SWToClientMessage, { payload: { organizationId: number } }>) => void;
type LoadStatusReply = { organizationId: number; status: LoadStatus; fetchedAt?: number };

/**
 * StateClient is a thin proxy in the main thread that communicates with
 * the Service Worker's StateManager via postMessage and BroadcastChannel.
 *
 * In Phase 1, this runs alongside Redux. The main thread pushes state
 * into the SW and listens for broadcasts — primarily for verification.
 */
export class StateClient {
  private broadcast: BroadcastChannel;
  private handlers = new Set<MessageHandler>();
  private swReady: Promise<ServiceWorkerRegistration> | undefined;

  constructor() {
    this.broadcast = new BroadcastChannel(STATE_CHANNEL_NAME);
    this.broadcast.onmessage = (event: MessageEvent<SWToClientMessage>) => {
      this.handlers.forEach((handler) => handler(event.data));
    };

    if ('serviceWorker' in navigator) {
      this.swReady = navigator.serviceWorker.ready;
    }
  }

  /**
   * Send a message to the Service Worker (fire-and-forget).
   */
  async postMessage(message: ClientToSWMessage): Promise<void> {
    const controller = navigator.serviceWorker?.controller;
    if (controller) {
      controller.postMessage(message);
      return;
    }

    // SW not yet controlling — wait for it
    if (this.swReady) {
      const registration = await this.swReady;
      const sw = registration.active;
      if (sw) {
        sw.postMessage(message);
      }
    }
  }

  /**
   * Send a message to the Service Worker and wait for a direct reply
   * via MessageChannel. Useful for request/response patterns.
   */
  async request<T extends SWToClientMessage>(message: ClientToSWMessage): Promise<T> {
    const channel = new MessageChannel();
    const replyPromise = new Promise<T>((resolve) => {
      channel.port1.onmessage = (event: MessageEvent<T>) => {
        resolve(event.data);
        channel.port1.close();
      };
    });

    const controller = navigator.serviceWorker?.controller;
    if (controller) {
      controller.postMessage(message, [channel.port2]);
    } else if (this.swReady) {
      const registration = await this.swReady;
      registration.active?.postMessage(message, [channel.port2]);
    }
    return replyPromise;
  }

  /**
   * Subscribe to state updates broadcast by the Service Worker.
   * Returns an unsubscribe function.
   */
  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Subscribe to broadcasts that carry an organizationId, filtered to a single org.
   * Returns an unsubscribe function.
   */
  subscribeToOrg(orgId: number, handler: OrgHandler): () => void {
    const wrapped: MessageHandler = (message) => {
      if ('payload' in message && (message.payload as { organizationId?: number }).organizationId === orgId) {
        handler(message as Parameters<OrgHandler>[0]);
      }
    };
    return this.onMessage(wrapped);
  }

  /**
   * Ask the SW for the current LoadStatus of an org via MessageChannel.
   * Resolves to 'idle' if the SW has no entry yet.
   */
  async requestLoadStatus(orgId: number): Promise<LoadStatusReply> {
    const reply = await this.request<SWToClientMessage>({
      type: 'REQUEST_LOAD_STATUS',
      payload: { organizationId: orgId },
    });
    if (reply.type === 'LOAD_STATUS') {
      return reply.payload;
    }
    return { organizationId: orgId, status: 'idle' };
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    this.handlers.clear();
    this.broadcast.close();
  }
}

let instance: StateClient | undefined;

/**
 * Get or create the singleton StateClient instance.
 */
export function getStateClient(): StateClient {
  if (!instance) {
    instance = new StateClient();
  }
  return instance;
}
